import {
  type AdminSource,
  ALL_CAPABILITIES,
  type ApiErrorCode,
  adminTeamIdOf,
  type ContractRecord,
  type FolderRecord,
  type PermissionGrantRecord,
  type PermissionLevel,
  type PermissionResourceType,
  type PrincipalType,
  type TeamMemberRecord,
  type TeamRecord,
  type UserRecord,
} from "@contract-manager/shared";

/**
 * The folder and team half of the development mock (`mockApi.ts`).
 *
 * It enforces the same refusals the API does — no cycles, no deleting
 * something that still holds things, no members from another domain — because
 * those are precisely the states the admin screens have to render, and a mock
 * that always said yes would leave them untested until deployment.
 */

const ORG_DOMAIN = "example.com";
const MOCK_USER = `you@${ORG_DOMAIN}`;
const ADMIN_TEAM_ID = adminTeamIdOf(ORG_DOMAIN);

/**
 * The deployment's standing administrators. `VITE_MOCK_ORDINARY_USER=1` takes
 * the mock user off it, which is how to see the screens as everyone else
 * does.
 */
const MOCK_ALLOWLIST: readonly string[] =
  import.meta.env.VITE_MOCK_ORDINARY_USER === "1" ? [] : [MOCK_USER];

function seedFolders(): FolderRecord[] {
  const now = new Date(Date.now() - 86_400_000).toISOString();
  const base = {
    orgDomain: ORG_DOMAIN,
    createdBy: MOCK_USER,
    createdAt: now,
    updatedAt: now,
  };
  return [
    { ...base, folderId: "f-nda", parentFolderId: null, name: "NDA" },
    { ...base, folderId: "f-service", parentFolderId: null, name: "業務委託" },
    {
      ...base,
      folderId: "f-service-jp",
      parentFolderId: "f-service",
      name: "国内",
    },
  ];
}

function seedTeams(): TeamRecord[] {
  const now = new Date(Date.now() - 86_400_000).toISOString();
  const base = {
    orgDomain: ORG_DOMAIN,
    createdBy: MOCK_USER,
    createdAt: now,
    updatedAt: now,
  };
  return [
    { ...base, teamId: ADMIN_TEAM_ID, parentTeamId: null, name: "管理者" },
    { ...base, teamId: "t-legal", parentTeamId: null, name: "法務" },
    {
      ...base,
      teamId: "t-legal-jp",
      parentTeamId: "t-legal",
      name: "法務（JP）",
    },
  ];
}

function seedMembers(): TeamMemberRecord[] {
  return [
    {
      // An admin by the team rather than the list, so both labels show.
      teamId: ADMIN_TEAM_ID,
      email: `sales@${ORG_DOMAIN}`,
      addedBy: MOCK_USER,
      addedAt: new Date(Date.now() - 86_400_000).toISOString(),
    },
    {
      teamId: "t-legal",
      email: `legal@${ORG_DOMAIN}`,
      addedBy: MOCK_USER,
      addedAt: new Date(Date.now() - 86_400_000).toISOString(),
    },
  ];
}

function seedUsers(): UserRecord[] {
  const earlier = new Date(Date.now() - 7 * 86_400_000).toISOString();
  const now = new Date().toISOString();
  return [
    {
      email: MOCK_USER,
      orgDomain: ORG_DOMAIN,
      displayName: "自分",
      createdAt: earlier,
      lastSignInAt: now,
    },
    {
      email: `legal@${ORG_DOMAIN}`,
      orgDomain: ORG_DOMAIN,
      displayName: "山田 太郎",
      createdAt: earlier,
      lastSignInAt: earlier,
    },
    {
      // No Google name: the directory falls back to the address.
      email: `sales@${ORG_DOMAIN}`,
      orgDomain: ORG_DOMAIN,
      displayName: null,
      createdAt: earlier,
      lastSignInAt: earlier,
    },
  ];
}

/** The translatable half of a refusal, shaped as the API sends it. */
export interface MockErrorDetail {
  readonly code: ApiErrorCode;
  readonly params?: Readonly<Record<string, string | number>>;
}

type Envelope = (
  data: unknown,
  status?: number,
  detail?: MockErrorDetail,
) => Response;

export interface OrganisationMock {
  /** Returns null when the request is not one of these routes. */
  handle(
    url: URL,
    init: RequestInit | undefined,
    envelope: Envelope,
  ): Response | null;
}

export function createOrganisationMock(
  contracts: ContractRecord[],
): OrganisationMock {
  const folders = seedFolders();
  const teams = seedTeams();
  const members = seedMembers();
  const users = seedUsers();
  // Nothing is granted to begin with, which is the state the screens have to
  // explain: closed by default, so only the owner and the admin team reach a
  // resource nobody has written a rule for.
  const grants: PermissionGrantRecord[] = [];
  let nextId = 1;

  const adminSourcesOf = (email: string): AdminSource[] => [
    ...(MOCK_ALLOWLIST.includes(email) ? (["ALLOWLIST"] as const) : []),
    ...(members.some((m) => m.teamId === ADMIN_TEAM_ID && m.email === email)
      ? (["ADMIN_TEAM"] as const)
      : []),
  ];

  /**
   * Changing folders, teams and memberships is for administrators, as in the
   * API. Runs after the permission and contract-folder routes, which have
   * rules of their own.
   */
  const refuseOrdinaryUser = (
    url: URL,
    init: RequestInit | undefined,
    envelope: Envelope,
  ): Response | null => {
    const method = init?.method ?? "GET";
    const reshapes =
      /\/(folders|teams)(\/|$)/.test(url.pathname) ||
      /\/users\/[^/]+\/teams/.test(url.pathname);
    if (method === "GET" || !reshapes || adminSourcesOf(MOCK_USER).length > 0) {
      return null;
    }
    return envelope("only an administrator can do this", 403, {
      code: "ADMIN_ONLY",
    });
  };

  const body = (init: RequestInit | undefined): Record<string, unknown> =>
    JSON.parse(String(init?.body ?? "{}")) as Record<string, unknown>;

  const descendantsOf = (
    id: string,
    parentOf: (node: { id: string }) => string | null,
    all: readonly { id: string }[],
  ): Set<string> => {
    const found = new Set<string>();
    const queue = [id];
    while (queue.length > 0) {
      const current = queue.pop() as string;
      for (const node of all) {
        if (parentOf(node) === current && !found.has(node.id)) {
          found.add(node.id);
          queue.push(node.id);
        }
      }
    }
    return found;
  };

  const handleFolders = (
    url: URL,
    init: RequestInit | undefined,
    envelope: Envelope,
  ): Response | null => {
    const method = init?.method ?? "GET";

    const contents = url.pathname.match(/\/folders\/([^/]+)\/contracts$/);
    if (contents) {
      const folderId = decodeURIComponent(contents[1] as string);
      return envelope({
        items: contracts
          .filter((record) => record.folderId === folderId)
          .map((record) => ({ ...record, access: "full" })),
      });
    }

    const one = url.pathname.match(/\/folders\/([^/]+)$/);
    if (one && (method === "PATCH" || method === "DELETE")) {
      const folderId = decodeURIComponent(one[1] as string);
      const index = folders.findIndex((f) => f.folderId === folderId);
      if (index === -1) {
        return envelope("folder not found", 404, {
          code: "NOT_FOUND",
          params: { resource: "folder" },
        });
      }

      if (method === "DELETE") {
        if (folders.some((f) => f.parentFolderId === folderId)) {
          return envelope("a folder with subfolders cannot be deleted", 409, {
            code: "FOLDER_HAS_SUBFOLDERS",
          });
        }
        if (contracts.some((record) => record.folderId === folderId)) {
          return envelope("a folder holding contracts cannot be deleted", 409, {
            code: "FOLDER_HAS_CONTRACTS",
          });
        }
        folders.splice(index, 1);
        return envelope({ folderId });
      }

      const changes = body(init);
      const parentId = changes.parentId as string | null | undefined;
      if (parentId !== undefined && parentId !== null) {
        const blocked = descendantsOf(
          folderId,
          (node) =>
            folders.find((f) => f.folderId === node.id)?.parentFolderId ?? null,
          folders.map((f) => ({ id: f.folderId })),
        );
        if (parentId === folderId || blocked.has(parentId)) {
          return envelope("a node cannot move under itself", 400, {
            code: "MOVE_INTO_DESCENDANT",
          });
        }
      }

      const current = folders[index] as FolderRecord;
      const updated: FolderRecord = {
        ...current,
        name: (changes.name as string | undefined) ?? current.name,
        parentFolderId:
          parentId === undefined ? current.parentFolderId : parentId,
        updatedAt: new Date().toISOString(),
      };
      folders[index] = updated;
      return envelope(updated);
    }

    if (url.pathname.endsWith("/folders") && method === "POST") {
      const input = body(init);
      const now = new Date().toISOString();
      const created: FolderRecord = {
        folderId: `f-new-${nextId++}`,
        orgDomain: ORG_DOMAIN,
        parentFolderId: (input.parentId as string | null | undefined) ?? null,
        name: String(input.name ?? "新しいフォルダ"),
        createdBy: MOCK_USER,
        createdAt: now,
        updatedAt: now,
      };
      folders.push(created);
      return envelope(created, 201);
    }

    if (url.pathname.endsWith("/folders") && method === "GET") {
      // The mock's user created every seeded folder, so they may do anything
      // to all of them.
      return envelope({
        items: folders.map((folder) => ({
          ...folder,
          access: ALL_CAPABILITIES,
        })),
      });
    }

    return null;
  };

  /**
   * Access grants for a folder or a contract.
   *
   * Deliberately does not enforce the rules the API does — owner, admin and
   * the lockout guard all need the caller's effective access, which the mock
   * has no notion of. What it reproduces is the list and the
   * replace-on-regrant behaviour, which is what the editor renders.
   *
   * `callerAccess` is everything, because the mock signs in as the person who
   * created everything it seeds.
   */
  const handlePermissions = (
    url: URL,
    init: RequestInit | undefined,
    envelope: Envelope,
  ): Response | null => {
    const match = url.pathname.match(
      /\/(folders|contracts)\/([^/]+)\/permissions(?:\/([^/]+)\/([^/]+))?$/,
    );
    if (!match) {
      return null;
    }

    const method = init?.method ?? "GET";
    const resourceType: PermissionResourceType =
      match[1] === "folders" ? "FOLDER" : "CONTRACT";
    const resourceId = decodeURIComponent(match[2] as string);
    const mine = () =>
      grants.filter(
        (grant) =>
          grant.resourceType === resourceType &&
          grant.resourceId === resourceId,
      );

    if (method === "GET") {
      return envelope({ items: mine(), callerAccess: ALL_CAPABILITIES });
    }

    if (method === "PUT") {
      const input = body(init);
      const principalType = input.principalType as PrincipalType;
      const principalId =
        principalType === "USER"
          ? String(input.principalId).toLowerCase()
          : String(input.principalId);
      const record: PermissionGrantRecord = {
        resourceType,
        resourceId,
        principalType,
        principalId,
        level: input.level as PermissionLevel,
        orgDomain: ORG_DOMAIN,
        grantedBy: MOCK_USER,
        grantedAt: new Date().toISOString(),
      };
      const existing = grants.findIndex(
        (grant) =>
          grant.resourceType === resourceType &&
          grant.resourceId === resourceId &&
          grant.principalType === principalType &&
          grant.principalId === principalId,
      );
      if (existing >= 0) {
        grants[existing] = record;
      } else {
        grants.push(record);
      }
      return envelope(record, 201);
    }

    if (method === "DELETE" && match[3] !== undefined) {
      const principalType = decodeURIComponent(match[3]) as PrincipalType;
      const principalId = decodeURIComponent(match[4] as string);
      const index = grants.findIndex(
        (grant) =>
          grant.resourceType === resourceType &&
          grant.resourceId === resourceId &&
          grant.principalType === principalType &&
          grant.principalId === principalId,
      );
      if (index >= 0) {
        grants.splice(index, 1);
      }
      return envelope({ resourceId, principalId });
    }

    return null;
  };

  const handleContractFolder = (
    url: URL,
    init: RequestInit | undefined,
    envelope: Envelope,
  ): Response | null => {
    const match = url.pathname.match(/\/contracts\/([^/]+)\/folder$/);
    if (!match || (init?.method ?? "GET") !== "PUT") {
      return null;
    }

    const contractId = decodeURIComponent(match[1] as string);
    const index = contracts.findIndex(
      (record) => record.contractId === contractId,
    );
    if (index === -1) {
      return envelope("contract not found", 404, {
        code: "NOT_FOUND",
        params: { resource: "contract" },
      });
    }

    const folderId = body(init).folderId as string | null;
    const current = contracts[index] as ContractRecord;
    const { folderId: _dropped, ...withoutFolder } = current;
    const updated: ContractRecord = {
      ...withoutFolder,
      ...(folderId === null ? {} : { folderId }),
      updatedAt: new Date().toISOString(),
    };
    contracts[index] = updated;
    return envelope({ ...updated, access: "full" });
  };

  const handleTeams = (
    url: URL,
    init: RequestInit | undefined,
    envelope: Envelope,
  ): Response | null => {
    const method = init?.method ?? "GET";

    const oneMember = url.pathname.match(/\/teams\/([^/]+)\/members\/([^/]+)$/);
    if (oneMember && method === "DELETE") {
      const teamId = decodeURIComponent(oneMember[1] as string);
      const email = decodeURIComponent(oneMember[2] as string).toLowerCase();
      const index = members.findIndex(
        (m) => m.teamId === teamId && m.email === email,
      );
      if (index >= 0) {
        members.splice(index, 1);
      }
      return envelope({ teamId, email });
    }

    const roster = url.pathname.match(/\/teams\/([^/]+)\/members$/);
    if (roster) {
      const teamId = decodeURIComponent(roster[1] as string);
      // No POST branch: joining a team goes through the user directory,
      // and a mock that still accepted it here would hide the fact that
      // the route no longer exists.
      return envelope({ items: members.filter((m) => m.teamId === teamId) });
    }

    const one = url.pathname.match(/\/teams\/([^/]+)$/);
    if (one && (method === "PATCH" || method === "DELETE")) {
      const teamId = decodeURIComponent(one[1] as string);
      const index = teams.findIndex((t) => t.teamId === teamId);
      if (index === -1) {
        return envelope("team not found", 404, {
          code: "NOT_FOUND",
          params: { resource: "team" },
        });
      }

      if (method === "DELETE") {
        if (teams.some((t) => t.parentTeamId === teamId)) {
          return envelope("a team with subteams cannot be deleted", 409, {
            code: "TEAM_HAS_SUBTEAMS",
          });
        }
        if (members.some((m) => m.teamId === teamId)) {
          return envelope("a team with members cannot be deleted", 409, {
            code: "TEAM_HAS_MEMBERS",
          });
        }
        teams.splice(index, 1);
        return envelope({ teamId });
      }

      const changes = body(init);
      const parentId = changes.parentId as string | null | undefined;
      if (parentId !== undefined && parentId !== null) {
        const blocked = descendantsOf(
          teamId,
          (node) =>
            teams.find((t) => t.teamId === node.id)?.parentTeamId ?? null,
          teams.map((t) => ({ id: t.teamId })),
        );
        if (parentId === teamId || blocked.has(parentId)) {
          return envelope("a node cannot move under itself", 400, {
            code: "MOVE_INTO_DESCENDANT",
          });
        }
      }

      const current = teams[index] as TeamRecord;
      const updated: TeamRecord = {
        ...current,
        name: (changes.name as string | undefined) ?? current.name,
        parentTeamId: parentId === undefined ? current.parentTeamId : parentId,
        updatedAt: new Date().toISOString(),
      };
      teams[index] = updated;
      return envelope(updated);
    }

    if (url.pathname.endsWith("/teams") && method === "POST") {
      const input = body(init);
      const now = new Date().toISOString();
      const created: TeamRecord = {
        teamId: `t-new-${nextId++}`,
        orgDomain: ORG_DOMAIN,
        parentTeamId: (input.parentId as string | null | undefined) ?? null,
        name: String(input.name ?? "新しいチーム"),
        createdBy: MOCK_USER,
        createdAt: now,
        updatedAt: now,
      };
      teams.push(created);
      return envelope(created, 201);
    }

    if (url.pathname.endsWith("/teams") && method === "GET") {
      return envelope({
        items: teams.map((team) => ({
          ...team,
          isAdminTeam: team.teamId === ADMIN_TEAM_ID,
        })),
      });
    }

    return null;
  };

  const handleUsers = (
    url: URL,
    init: RequestInit | undefined,
    envelope: Envelope,
  ): Response | null => {
    const method = init?.method ?? "GET";

    if (url.pathname.endsWith("/me")) {
      const me = users.find((user) => user.email === MOCK_USER);
      return envelope({
        ...me,
        lastSignInAt: new Date().toISOString(),
        adminSources: adminSourcesOf(MOCK_USER),
      });
    }

    const oneTeam = url.pathname.match(/\/users\/([^/]+)\/teams\/([^/]+)$/);
    if (oneTeam && method === "DELETE") {
      const email = decodeURIComponent(oneTeam[1] as string).toLowerCase();
      const teamId = decodeURIComponent(oneTeam[2] as string);
      const index = members.findIndex(
        (m) => m.email === email && m.teamId === teamId,
      );
      if (index >= 0) {
        members.splice(index, 1);
      }
      return envelope({ email, teamId });
    }

    const userTeams = url.pathname.match(/\/users\/([^/]+)\/teams$/);
    if (userTeams) {
      const email = decodeURIComponent(userTeams[1] as string).toLowerCase();
      if (!users.some((user) => user.email === email)) {
        return envelope("user not found", 404, {
          code: "NOT_FOUND",
          params: { resource: "user" },
        });
      }

      if (method === "POST") {
        const teamId = String(body(init).teamId ?? "");
        if (!teams.some((team) => team.teamId === teamId)) {
          return envelope("team not found", 404, {
            code: "NOT_FOUND",
            params: { resource: "team" },
          });
        }
        const record: TeamMemberRecord = {
          teamId,
          email,
          addedBy: MOCK_USER,
          addedAt: new Date().toISOString(),
        };
        const existing = members.findIndex(
          (m) => m.teamId === teamId && m.email === email,
        );
        if (existing >= 0) {
          members[existing] = record;
        } else {
          members.push(record);
        }
        return envelope(record, 201);
      }

      return envelope({
        items: members
          .filter((m) => m.email === email)
          .map((m) => ({
            teamId: m.teamId,
            name: teams.find((t) => t.teamId === m.teamId)?.name ?? "",
            addedAt: m.addedAt,
            addedBy: m.addedBy,
          }))
          .filter((entry) => entry.name !== ""),
      });
    }

    if (url.pathname.endsWith("/users") && method === "GET") {
      return envelope({
        items: users.map((user) => ({
          ...user,
          adminSources: adminSourcesOf(user.email),
        })),
      });
    }

    return null;
  };

  return {
    handle(url, init, envelope) {
      return (
        // Before the folder and contract routes: both `/folders/<id>/...` and
        // `/contracts/<id>/...` would otherwise match their own handlers.
        handlePermissions(url, init, envelope) ??
        handleContractFolder(url, init, envelope) ??
        refuseOrdinaryUser(url, init, envelope) ??
        handleUsers(url, init, envelope) ??
        handleFolders(url, init, envelope) ??
        handleTeams(url, init, envelope)
      );
    },
  };
}
